import { computed, observable, runInAction } from 'mobx';
import { Product, ProductDetailsHolder } from '../content/ProductInterfaces';
import ErrorUtil from '../error/ErrorUtil';
import { FetchStatus } from '../util/FetchStatus';
import { createTransformer } from 'mobx-utils';
import ShopAPI from '../util/ShopApiUtil';
import ProductPriceStore from './ProductPriceStore';
import RootStore from './RootStore';
import SsoStore from './SsoStore';

const FETCH_IN_PROGRESS_STATUSES = [FetchStatus.FETCHING, FetchStatus.UNINITIALIZED];
class ProductDetailsStore {
  @observable accessor status: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor products: ProductDetailsHolder[] = [];

  private ssoStore: SsoStore;
  private productPriceStore: ProductPriceStore;

  public constructor(rootStore: RootStore) {
    this.ssoStore = rootStore.ssoStore;
    this.productPriceStore = rootStore.productPriceStore;
  }

  public async loadProducts(group: string, productCodes: string[]) {
    runInAction(() => {
      this.status = FetchStatus.FETCHING;
    });
    await Promise.all(
      productCodes.map(async (code) => {
        runInAction(() => {
          if (!this.products.some((p) => p.product?.productCode === code)) {
            this.products.push({
              status: FetchStatus.UNINITIALIZED,
              product: { productCode: code } as Product,
            });
          }
        });
        if (this.fetchNotRequired(code)) {
          return;
        }
        runInAction(() => {
          this.getProductHolder(code).status = FetchStatus.FETCHING;
        });
        const product = await this.fetchProduct(group, code);
        runInAction(() => {
          this.getProductHolder(code).status = FetchStatus.FETCHED;
          this.getProductHolder(code).product = product;
        });
      }),
    );
    await this.productPriceStore.fetchPrices(productCodes, this.productsToShow(productCodes));
    runInAction(() => {
      this.status = FetchStatus.FETCHED;
    });
  }

  private fetchNotRequired(code: string) {
    return [FetchStatus.FETCHING, FetchStatus.FETCHED].includes(this.getProductHolder(code).status);
  }

  private getProductHolder(code: string) {
    return this.products.find((c) => c.product?.productCode === code);
  }

  private async fetchProduct(group: string, productCode: string): Promise<Product> {
    try {
      const response = await ShopAPI.get(`/products/${group}/details`, {
        params: {
          productCode,
          clientType: this.ssoStore.customerType,
        },
      });
      if (!response.data) {
        return null;
      }
      return response.data;
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      return null;
    }
  }

  @computed
  public get productsToShow() {
    return createTransformer((productCodes: string[]) =>
      this.products.filter((holder) => productCodes.includes(holder?.product?.productCode)).map((p) => p.product),
    );
  }

  public isFetching() {
    return FETCH_IN_PROGRESS_STATUSES.includes(this.status);
  }
}

export default ProductDetailsStore;
